/**
 * PlacementTableRow - Individual row in PlacementTableView
 *
 * Compact 38-42px row with expand/collapse functionality.
 * Shows status, tail number, aircraft info, spot assignment, and times.
 */

import { Box, Collapse, Typography } from "@mui/material";
import React from "react";
import { Spot, Zone } from "../../../Ramp/TemporalTest/types";
import { TableRow as PlacementRow } from "../utils/buildTableRows";
import { EditableSpotCell } from "./EditableSpotCell";
import { EditableTimeCell } from "./EditableTimeCell";
import { PlacementTableRowExpanded } from "./PlacementTableRowExpanded";
import { ScheduleChange } from "./SpotOccupancyGantt";

interface PlacementTableRowProps {
  row: PlacementRow;
  isExpanded: boolean;
  onRowClick: (aircraftId: string) => void;
  enableEditing: boolean;
  onScheduleChanged?: (changes: ScheduleChange[]) => void;
  spots: Spot[];
  zones: Zone[];
  spotIdToName: Record<string, string>;
}

export const PlacementTableRow: React.FC<PlacementTableRowProps> = ({
  row,
  isExpanded,
  onRowClick,
  enableEditing,
  onScheduleChanged,
  spots,
  zones,
  spotIdToName,
}) => {
  // Status badge styling
  const getStatusStyle = () => {
    switch (row.status) {
      case "placed":
        return {
          bgcolor: "rgba(46, 125, 50, 0.06)",
          borderColor: "rgba(46, 125, 50, 0.20)",
          color: "#2e7d32",
        };
      case "partial":
        return {
          bgcolor: "rgba(237, 108, 2, 0.06)",
          borderColor: "rgba(237, 108, 2, 0.20)",
          color: "#ed6c02",
        };
      case "unplaced":
        return {
          bgcolor: "rgba(211, 47, 47, 0.06)",
          borderColor: "rgba(211, 47, 47, 0.20)",
          color: "#d32f2f",
        };
    }
  };

  const statusStyle = getStatusStyle();

  return (
    <>
      {/* Main Row - Grid Layout */}
      <Box
        onClick={() => onRowClick(row.aircraftId)}
        sx={{
          display: "grid",
          gridTemplateColumns: "100px 180px 1fr 130px 130px 90px",
          gap: 2,
          alignItems: "center",
          height: 48,
          px: 2,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          cursor: "pointer",
          transition:
            "background-color 80ms ease-in-out, box-shadow 80ms ease-in-out",
          "&:hover": {
            bgcolor: "rgba(0,0,0,0.05)",
            boxShadow: "inset 0 1px 0 0 rgba(0, 0, 0, 0.03)",
          },
        }}
      >
        {/* Status Column */}
        <Box>
          <Box
            sx={{
              px: 1,
              py: 0.375,
              borderRadius: 1.5,
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "lowercase",
              display: "inline-block",
              ...statusStyle,
              border: "1px solid",
            }}
          >
            {row.status}
          </Box>
        </Box>

        {/* Tail Number + Aircraft Column */}
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
          >
            {row.isLocked && <Typography sx={{ fontSize: 10 }}>🔒</Typography>}
            {row.placementSource === "manual" && (
              <Typography sx={{ fontSize: 10 }}>✋</Typography>
            )}
            <Typography
              sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary" }}
            >
              {row.tailNumber}
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", opacity: 0.7 }}
          >
            {row.aircraftMake} {row.aircraftModel}
          </Typography>
        </Box>

        {/* Spot(s) Column */}
        <Box>
          <EditableSpotCell
            row={row}
            spots={spots}
            zones={zones}
            spotIdToName={spotIdToName}
            enableEditing={enableEditing}
            onScheduleChanged={onScheduleChanged}
          />
        </Box>

        {/* Arrival Column */}
        <Box>
          <EditableTimeCell
            row={row}
            field="arrival"
            enableEditing={enableEditing}
            onScheduleChanged={onScheduleChanged}
          />
        </Box>

        {/* Departure Column */}
        <Box>
          <EditableTimeCell
            row={row}
            field="departure"
            enableEditing={enableEditing}
            onScheduleChanged={onScheduleChanged}
          />
        </Box>

        {/* Dwell Column */}
        <Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "text.secondary",
              fontFamily: "SF Mono, Consolas, monospace",
            }}
          >
            {row.dwellFormatted}
          </Typography>
        </Box>
      </Box>

      {/* Expanded Row Content */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <PlacementTableRowExpanded row={row} spotIdToName={spotIdToName} />
      </Collapse>
    </>
  );
};
